import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, UserCheck, Globe, Mail, Trash2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';

const PrivacyPage = () => {
  const lastUpdate = "6 février 2025";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            Retour à l&apos;accueil
          </Link>
          <Link to="/" className="font-['Barlow_Condensed'] text-2xl font-bold text-orange-500">
            COLISFLASH
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
              <Shield className="w-8 h-8 text-blue-500" />
            </div>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-slate-900 mb-2">
              Politique de Confidentialité
            </h1>
            <p className="text-slate-500">Dernière mise à jour : {lastUpdate}</p>
          </div>

          {/* Introduction */}
          <div className="bg-blue-50 rounded-xl p-6 mb-10 border border-blue-100">
            <p className="text-slate-700 leading-relaxed">
              Chez COLISFLASH, nous accordons une grande importance à la protection de vos données personnelles. 
              Cette politique de confidentialité vous informe sur la manière dont nous collectons, utilisons et 
              protégeons vos informations conformément au <strong>Règlement Général sur la Protection des Données (RGPD)</strong>.
            </p>
          </div>

          {/* Table of Contents */}
          <div className="bg-slate-50 rounded-xl p-6 mb-10">
            <h2 className="font-semibold text-slate-900 mb-4">Sommaire</h2>
            <nav className="space-y-2 text-sm">
              <a href="#section-1" className="block text-blue-600 hover:text-blue-700">1. Responsable du traitement</a>
              <a href="#section-2" className="block text-blue-600 hover:text-blue-700">2. Données collectées</a>
              <a href="#section-3" className="block text-blue-600 hover:text-blue-700">3. Finalités du traitement</a>
              <a href="#section-4" className="block text-blue-600 hover:text-blue-700">4. Base légale du traitement</a>
              <a href="#section-5" className="block text-blue-600 hover:text-blue-700">5. Destinataires des données</a>
              <a href="#section-6" className="block text-blue-600 hover:text-blue-700">6. Durée de conservation</a>
              <a href="#section-7" className="block text-blue-600 hover:text-blue-700">7. Sécurité des données</a>
              <a href="#section-8" className="block text-blue-600 hover:text-blue-700">8. Vos droits</a>
              <a href="#section-9" className="block text-blue-600 hover:text-blue-700">9. Cookies</a>
              <a href="#section-10" className="block text-blue-600 hover:text-blue-700">10. Modifications</a>
              <a href="#section-11" className="block text-blue-600 hover:text-blue-700">11. Contact</a>
            </nav>
          </div>

          {/* Sections */}
          <div className="prose prose-slate max-w-none">
            
            {/* Section 1 */}
            <section id="section-1" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-blue-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">1. Responsable du traitement</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Le responsable du traitement de vos données personnelles est :
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-slate-700 m-0">
                  <strong>COLISFLASH SAS</strong><br />
                  Siège social : Paris, France<br />
                  Email : <a href="mailto:privacy@colisflash.com" className="text-blue-600">privacy@colisflash.com</a><br />
                  Site web : <a href="https://colisflash.com" className="text-blue-600">colisflash.com</a>
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section id="section-2" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Database className="w-5 h-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">2. Données collectées</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Nous collectons les catégories de données suivantes :
              </p>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">2.1 Données d&apos;identification</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Nom et prénom</li>
                <li>Adresse email</li>
                <li>Numéro de téléphone</li>
                <li>Photo de profil (optionnelle)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">2.2 Données de vérification d&apos;identité</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Copie de pièce d&apos;identité (CNI, passeport, permis)</li>
                <li>Photo selfie pour vérification</li>
                <li>Ces données sont traitées par notre partenaire <strong>Stripe Identity</strong></li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">2.3 Données de transaction</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Historique des colis envoyés/transportés</li>
                <li>Adresses d&apos;enlèvement et de livraison</li>
                <li>Montants des transactions</li>
                <li>IBAN (pour les cotransporteurs)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">2.4 Données de paiement</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Informations de carte bancaire (traitées par Stripe)</li>
                <li>Historique des paiements</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-slate-800 mt-6">2.5 Données de connexion</h3>
              <ul className="text-slate-600 space-y-2">
                <li>Adresse IP</li>
                <li>Type de navigateur et appareil</li>
                <li>Pages visitées et durée de visite</li>
              </ul>
            </section>

            {/* Section 3 */}
            <section id="section-3" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-purple-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">3. Finalités du traitement</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Vos données sont utilisées pour :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Gestion de votre compte :</strong> Création, authentification et personnalisation</li>
                <li><strong>Mise en relation :</strong> Connexion expéditeurs et cotransporteurs</li>
                <li><strong>Traitement des transactions :</strong> Paiements et versements</li>
                <li><strong>Vérification d&apos;identité :</strong> Renforcement de la confiance entre utilisateurs</li>
                <li><strong>Communication :</strong> Notifications, confirmations, support client</li>
                <li><strong>Amélioration du service :</strong> Analyses statistiques anonymisées</li>
                <li><strong>Obligations légales :</strong> Facturation, comptabilité, lutte contre la fraude</li>
              </ul>
            </section>

            {/* Section 4 */}
            <section id="section-4" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">4. Base légale du traitement</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Conformément au RGPD, nos traitements reposent sur :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>L&apos;exécution du contrat (Art. 6.1.b) :</strong> Pour la fourniture de nos services</li>
                <li><strong>Le consentement (Art. 6.1.a) :</strong> Pour la vérification d&apos;identité et les communications marketing</li>
                <li><strong>L&apos;obligation légale (Art. 6.1.c) :</strong> Pour la facturation et la comptabilité</li>
                <li><strong>L&apos;intérêt légitime (Art. 6.1.f) :</strong> Pour la prévention de la fraude et l&apos;amélioration du service</li>
              </ul>
            </section>

            {/* Section 5 */}
            <section id="section-5" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                  <Globe className="w-5 h-5 text-cyan-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">5. Destinataires des données</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Vos données peuvent être partagées avec :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Autres utilisateurs :</strong> Informations nécessaires à la transaction (nom, note, badge vérifié)</li>
                <li><strong>Stripe :</strong> Paiements et vérification d&apos;identité (certifié PCI-DSS)</li>
                <li><strong>Resend :</strong> Envoi d&apos;emails transactionnels</li>
                <li><strong>Google Maps :</strong> Calcul de distances et autocomplétion d&apos;adresses</li>
                <li><strong>Autorités :</strong> En cas d&apos;obligation légale</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Nous ne vendons jamais vos données à des tiers à des fins publicitaires.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">6. Durée de conservation</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Vos données sont conservées :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Données de compte :</strong> Pendant la durée de votre inscription + 3 ans après suppression</li>
                <li><strong>Données de transaction :</strong> 10 ans (obligations comptables)</li>
                <li><strong>Données de vérification d&apos;identité :</strong> 5 ans (obligations anti-blanchiment)</li>
                <li><strong>Données de connexion :</strong> 1 an</li>
                <li><strong>Messages :</strong> 3 ans après la fin de la transaction</li>
              </ul>
            </section>

            {/* Section 7 */}
            <section id="section-7" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Lock className="w-5 h-5 text-red-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">7. Sécurité des données</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Nous mettons en œuvre des mesures de sécurité appropriées :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Chiffrement :</strong> Toutes les communications sont chiffrées (HTTPS/TLS)</li>
                <li><strong>Authentification :</strong> Tokens JWT sécurisés avec expiration</li>
                <li><strong>Stockage :</strong> Mots de passe hashés (bcrypt), données sensibles chiffrées</li>
                <li><strong>Paiements :</strong> Aucune donnée bancaire stockée sur nos serveurs (Stripe)</li>
                <li><strong>Accès :</strong> Contrôle strict des accès aux données</li>
                <li><strong>Hébergement :</strong> Serveurs sécurisés en Union Européenne</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section id="section-8" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-indigo-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">8. Vos droits</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Conformément au RGPD, vous disposez des droits suivants :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Droit d&apos;accès :</strong> Obtenir une copie de vos données</li>
                <li><strong>Droit de rectification :</strong> Corriger des données inexactes</li>
                <li><strong>Droit à l&apos;effacement :</strong> Demander la suppression de vos données</li>
                <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                <li><strong>Droit d&apos;opposition :</strong> Vous opposer à certains traitements</li>
                <li><strong>Droit à la limitation :</strong> Restreindre le traitement de vos données</li>
                <li><strong>Droit de retirer votre consentement :</strong> À tout moment pour les traitements basés sur le consentement</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Pour exercer ces droits, contactez-nous à <a href="mailto:privacy@colisflash.com" className="text-blue-600">privacy@colisflash.com</a>. 
                Nous répondrons dans un délai d&apos;un mois.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                Vous pouvez également déposer une réclamation auprès de la <strong>CNIL</strong> (Commission Nationale de l&apos;Informatique et des Libertés).
              </p>
            </section>

            {/* Section 9 */}
            <section id="section-9" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-pink-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">9. Cookies</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Nous utilisons des cookies pour :
              </p>
              <ul className="text-slate-600 space-y-3">
                <li><strong>Cookies essentiels :</strong> Fonctionnement du site (authentification, panier)</li>
                <li><strong>Cookies analytiques :</strong> Mesure d&apos;audience anonymisée</li>
                <li><strong>Cookies fonctionnels :</strong> Mémorisation de vos préférences</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur.
              </p>
            </section>

            {/* Section 10 */}
            <section id="section-10" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-slate-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">10. Modifications</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Nous pouvons modifier cette politique de confidentialité à tout moment. En cas de modification 
                substantielle, nous vous en informerons par email ou via une notification sur la plateforme.
              </p>
              <p className="text-slate-600 leading-relaxed mt-4">
                La date de dernière mise à jour est indiquée en haut de ce document.
              </p>
            </section>

            {/* Section 11 */}
            <section id="section-11" className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-500" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 m-0">11. Contact</h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                Pour toute question concernant cette politique de confidentialité ou vos données personnelles :
              </p>
              <div className="bg-slate-50 rounded-lg p-4 mt-4">
                <p className="text-slate-700 m-0">
                  <strong>Délégué à la Protection des Données (DPO)</strong><br />
                  Email : <a href="mailto:privacy@colisflash.com" className="text-blue-600">privacy@colisflash.com</a><br />
                  Adresse : COLISFLASH SAS, Paris, France
                </p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-slate-200 text-center">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} COLISFLASH - Tous droits réservés
            </p>
            <div className="mt-4 flex justify-center gap-4">
              <Link to="/cgu">
                <Button variant="outline" className="rounded-full">
                  Voir les CGU
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-orange-500 hover:bg-orange-600 rounded-full">
                  Retour à l&apos;accueil
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
